import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Building2, Calendar, UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { GetMyPaymentsResponseDto } from '../../dtos/responses/get-my-payments.response.dto';
import { formatDate } from '../../lib/date';
import { useGetMyPayments, useRequestRefund } from '../../query/payment';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';

// 서버는 PAID/CANCELED만 내려준다(READY·FAILED는 결제하다 만 흔적이라 내역이 아님).
// 방어적으로 라벨은 유지하되, 실제로 노출되는 건 아래 두 가지다.
const PAYMENT_STATUS_META: Record<
  GetMyPaymentsResponseDto['status'],
  { label: string; className: string }
> = {
  PAID: { label: '결제완료', className: 'bg-emerald-100 text-emerald-700' },
  CANCELED: { label: '환불완료', className: 'bg-gray-100 text-gray-600' },
  READY: { label: '대기', className: 'bg-gray-100 text-gray-600' },
  FAILED: { label: '실패', className: 'bg-red-100 text-red-700' },
};

const MyPaymentsPage = () => {
  const { data } = useGetMyPayments();
  const payments = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-1 lg:p-8">
      <h1 className="text-xl font-bold">결제 / 환불 내역</h1>
      {payments.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground">
          결제 내역이 없습니다.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {payments.map(p => (
            <PaymentCard key={p.coursePaymentId} payment={p} />
          ))}
        </div>
      )}
    </div>
  );
};

interface PaymentCardProps {
  payment: GetMyPaymentsResponseDto;
}

const PaymentCard = ({ payment }: PaymentCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const { mutate: requestRefund, isPending } = useRequestRefund();
  const queryClient = useQueryClient();

  const statusMeta = PAYMENT_STATUS_META[payment.status];
  const refund = payment.refund;

  // 환불 신청 가능: 결제완료 상태이고, 진행 중인 신청이 없을 때(없거나 거절됨 → 재신청 가능).
  const canRequest = payment.status === 'PAID' && (!refund || refund.status === 'REJECTED');

  const handleSubmit = () => {
    if (!reason.trim()) {
      toast.error('환불 사유를 입력해주세요.');
      return;
    }
    requestRefund(
      { coursePaymentId: payment.coursePaymentId, reason: reason.trim() },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['student', 'payment', 'my'] });
          toast.success('환불 신청이 접수되었습니다.');
          setExpanded(false);
          setReason('');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => toast.error(error?.message ?? '환불 신청에 실패했습니다.'),
      },
    );
  };

  return (
    <Card className="bg-white">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="text-base font-semibold">{payment.courseName}</div>
            {/* 같은 이름의 강좌가 여러 개일 수 있어, 어떤 강좌를 결제한 건지 식별할 정보를 함께 보여준다. */}
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">
                  {payment.course.school} {payment.course.department}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <UserRound className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{payment.course.professorName} 교수</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <span>
                  {payment.course.year}년 {payment.course.semester} · 결제일{' '}
                  {formatDate(payment.createdAt as string)}
                </span>
              </div>
            </div>
          </div>
          {/* 상태 배지와 환불 신청 버튼을 같은 열에 둔다. 버튼을 별도 행으로 내리면
              내용이 2줄뿐인 카드에 빈 공간만 크게 생긴다. */}
          <div className="flex flex-col items-end gap-2">
            <span className="text-base font-bold">{payment.amount.toLocaleString()}원</span>
            <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
            {refund?.status === 'PENDING' && (
              <Badge className="bg-amber-100 text-amber-700">환불 대기중</Badge>
            )}
            {refund?.status === 'REJECTED' && (
              <Badge className="bg-red-100 text-red-700">환불 거절</Badge>
            )}
            {canRequest && !expanded && (
              <Button type="button" variant="outline" size="sm" onClick={() => setExpanded(true)}>
                환불 신청
              </Button>
            )}
          </div>
        </div>

        {refund?.status === 'REJECTED' && refund.adminNote && (
          <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            거절 사유: {refund.adminNote}
          </div>
        )}

        {canRequest && expanded && (
          <div className="mt-4 space-y-3">
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="환불 사유를 입력해주세요."
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isPending}
                onClick={() => {
                  setExpanded(false);
                  setReason('');
                }}
              >
                취소
              </Button>
              <Button type="button" size="sm" disabled={isPending} onClick={handleSubmit}>
                환불 신청하기
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyPaymentsPage;
